import axios from '@/services/axios';

const BASE_PATH =
  '/financeiro/cobrancas/lotes';

const ACOES =
  Object.freeze([
    'ENVIO_BOLETOS',
    'COMPARTILHAMENTO_ANTECIPADO'
  ]);

const CANAIS =
  Object.freeze([
    'EMAIL',
    'WHATSAPP_WEB'
  ]);

function isPlainObject(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value)
  );
}

function normalizePositiveId(
  value,
  fieldName = 'ID'
) {
  const text =
    String(
      value ?? ''
    ).trim();

  if (
    !/^[1-9][0-9]*$/.test(
      text
    )
  ) {
    throw new TypeError(
      `${fieldName} inválido.`
    );
  }

  const parsed =
    Number(text);

  if (
    !Number.isSafeInteger(
      parsed
    ) ||
    parsed <= 0
  ) {
    throw new TypeError(
      `${fieldName} inválido.`
    );
  }

  return parsed;
}

function cleanParams(
  params = {}
) {
  if (!isPlainObject(params)) {
    throw new TypeError(
      'Parâmetros de lotes inválidos.'
    );
  }

  return Object.fromEntries(
    Object.entries(params)
      .filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== ''
      )
  );
}

function normalizePagination(
  value
) {
  const source =
    isPlainObject(value)
      ? value
      : {};

  return {
    pagina:
      Number(
        source.pagina ??
        1
      ),

    limite:
      Number(
        source.limite ??
        20
      ),

    total_itens:
      Number(
        source.total_itens ??
        0
      ),

    total_paginas:
      Number(
        source.total_paginas ??
        0
      )
  };
}

async function listar(
  params = {}
) {
  const response =
    await axios.get(
      BASE_PATH,
      {
        params:
          cleanParams(
            params
          )
      }
    );

  const data =
    response.data;

  return {
    data:
      Array.isArray(
        data?.data
      )
        ? data.data
        : [],

    paginacao:
      normalizePagination(
        data?.paginacao
      )
  };
}

async function detalhar(
  id
) {
  const loteId =
    normalizePositiveId(
      id,
      'ID do lote'
    );

  const response =
    await axios.get(
      `${BASE_PATH}/${loteId}`
    );

  return response.data;
}

function normalizeDistribuicaoInput(
  input
) {
  if (!isPlainObject(input)) {
    throw new TypeError(
      'Dados de distribuição inválidos.'
    );
  }

  const allowed =
    new Set([
      'acao',
      'canais',
      'expira_em'
    ]);

  for (
    const key of
    Object.keys(input)
  ) {
    if (!allowed.has(key)) {
      throw new TypeError(
        `Campo de distribuição desconhecido: ${key}.`
      );
    }
  }

  if (
    !ACOES.includes(
      input.acao
    )
  ) {
    throw new TypeError(
      'Ação de distribuição inválida.'
    );
  }

  if (
    !Array.isArray(
      input.canais
    ) ||
    input.canais.length === 0
  ) {
    throw new TypeError(
      'Informe ao menos um canal de distribuição.'
    );
  }

  const canais =
    [];

  const vistos =
    new Set();

  for (
    const canal of
      input.canais
  ) {
    if (
      !CANAIS.includes(
        canal
      )
    ) {
      throw new TypeError(
        'Canal de distribuição inválido.'
      );
    }

    if (
      vistos.has(
        canal
      )
    ) {
      throw new TypeError(
        'Canal de distribuição duplicado.'
      );
    }

    vistos.add(
      canal
    );

    canais.push(
      canal
    );
  }

  const body = {
    acao:
      input.acao,

    canais
  };

  if (
    Object.prototype
      .hasOwnProperty.call(
        input,
        'expira_em'
      ) &&
    input.expira_em !==
      undefined
  ) {
    if (
      input.expira_em !==
        null &&
      typeof input.expira_em !==
        'string'
    ) {
      throw new TypeError(
        'Expiração da distribuição inválida.'
      );
    }

    body.expira_em =
      input.expira_em;
  }

  return body;
}

async function distribuir(
  id,
  input
) {
  const loteId =
    normalizePositiveId(
      id,
      'ID do lote'
    );

  const body =
    normalizeDistribuicaoInput(
      input
    );

  const response =
    await axios.post(
      `${BASE_PATH}/${loteId}/distribuicao`,
      body
    );

  return response.data;
}

export const lotesCobrancasApi = {
  listar,
  detalhar,
  distribuir
};

export {
  ACOES,
  CANAIS,
  BASE_PATH,
  normalizePositiveId,
  cleanParams,
  normalizeDistribuicaoInput
};

export default lotesCobrancasApi;
